/**
 * Home — pantalla de aterrizaje del Pórtico (rediseño v2, dirección «Mesa» de
 * Claude Design). #280 / Home-equipo-departamentos.
 *
 * Es el estado inicial de una conversación nueva sin dirigir (borrador que
 * enruta Anne). Saludo editorial + compositor de línea «¿Qué necesitas hoy?»
 * arriba; debajo, el equipo DISPONIBLE como tarjetas ficha (foto vertical
 * grande + nombre + rol); un clic abre un chat dirigido al agente. Al final,
 * «continuar donde lo dejaste». Todo data-driven desde el descriptor (R-PT-02).
 * Las fotos las resuelve avatarUrlOf por id en el FRONT (lib/images.js) —
 * misma fuente única que el icono del rail y el logo del tenant (GEN-11),
 * mismo patrón que los colores. El kernel no emite URLs de imagen.
 */

import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { AgentAvatar } from './AgentAvatar.jsx'
import AiDisclosureNotice from './AiDisclosureNotice.jsx'
import AutoGrowTextarea from './AutoGrowTextarea.jsx'
import { agentColorOf } from '../lib/agent-colors.js'
import { avatarUrlOf } from '../lib/images.js'
import { groupByDepartment } from '../lib/agent-groups.js'

function saludo() {
  const h = new Date().getHours()
  if (h < 6)  return 'Buenas noches'
  if (h < 14) return 'Buenos días'
  if (h < 21) return 'Buenas tardes'
  return 'Buenas noches'
}

function primerNombre(name) {
  return (name ?? '').trim().split(/\s+/)[0] || ''
}

function initials(name) {
  return (name ?? '')
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase()
}

function relativa(iso) {
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  const mins  = Math.floor((Date.now() - t) / 60_000)
  const hours = Math.floor(mins / 60)
  const days  = Math.floor(hours / 24)
  if (mins < 60)  return `hace ${mins} min`
  if (hours < 24) return `hace ${hours} h`
  return days === 1 ? 'ayer' : `hace ${days} d`
}

// Tarjeta ficha — foto vertical grande («foto extendida»), nombre y rol, como
// el diseño de Claude Design. Clic -> chat dirigido a ese agente. La inicial
// vive SIEMPRE debajo y la foto se pinta encima: si el asset del id no existe
// (p. ej. Koana), la imagen se oculta y queda la inicial sin salto de layout.
function TeamCard({ agent, ringColor, onSelect }) {
  const puedeIniciarChat = agent.puede_iniciar_chat !== false
  const photo = avatarUrlOf(agent)
  return (
    <button
      type="button"
      className={'team-card' + (puedeIniciarChat ? '' : ' team-card--disabled')}
      onClick={puedeIniciarChat ? () => onSelect(agent) : undefined}
      disabled={!puedeIniciarChat}
      title={puedeIniciarChat ? agent.descripcion : `${agent.nombre} — próximamente`}
    >
      <span className="team-card__photo">
        <span className="team-card__photo-ini" style={{ background: ringColor || 'var(--brand-solid)' }}>{initials(agent.nombre)}</span>
        {photo && (
          <img
            src={photo}
            alt=""
            decoding="async"
            onError={e => { e.currentTarget.style.display = 'none' }}
          />
        )}
      </span>
      <span className="team-card__body">
        <span className="team-card__name">{agent.nombre}</span>
        {agent.rol && <span className="team-card__role">{agent.rol}</span>}
        {!puedeIniciarChat && <span className="team-card__soon">Próximamente</span>}
      </span>
    </button>
  )
}

function ChatCard({ chat, agents, palette, theme, onOpen }) {
  const participantes = (chat.agentes_en_chat ?? []).slice(0, 3)
  return (
    <button className="home-card" onClick={() => onOpen(chat.chat_id)} title={chat.de_que_va}>
      <span className="home-card__title">{chat.titulo ?? '(sin título)'}</span>
      {chat.de_que_va && <span className="home-card__desc">{chat.de_que_va}</span>}
      <span className="home-card__foot">
        <span className="home-card__avatars">
          {participantes.map(p => {
            const a = agents.find(x => x.nombre === p.nombre)
            return (
              <AgentAvatar
                key={p.nombre}
                name={p.nombre}
                avatarUrl={avatarUrlOf(a)}
                size={22}
                ringColor={a ? agentColorOf(palette ?? {}, a.id, theme) : null}
              />
            )
          })}
        </span>
        <span className="home-card__time">{relativa(chat.updated_at)}</span>
      </span>
    </button>
  )
}

export default function Home({
  user, agents = [], chats = [], palette, theme,
  onSend, onSelectAgent, onSelectChat,
}) {
  const [text, setText] = useState('')

  // Solo los agentes DISPONIBLES (operativos) en la entrada — «empezar una
  // conversación con los agentes disponibles» (Guo 2026-07-16). El resto del
  // roster viaja en el descriptor y aparece aquí en cuanto el kernel lo marque
  // disponible. Se mantiene la agrupación por departamento (data-driven); la
  // cabecera de departamento solo se pinta si hay más de uno.
  const disponibles = agents.filter(a => a.puede_iniciar_chat !== false)
  const grupos = groupByDepartment(disponibles)
  const orquestadora = agents.find(a => /orquest/i.test(a.rol ?? '')) ?? agents[0]
  const enrutadora = orquestadora?.nombre ?? 'Anne'
  const recientes = chats.slice(0, 3)
  const nombre = primerNombre(user?.display_name)

  function enviar() {
    const t = text.trim()
    if (!t) return
    setText('')
    onSend(t)
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      enviar()
    }
  }

  return (
    <div className="home">
      <div className="home__inner">
        <header className="home__head">
          <span className="home__overline">{saludo()}</span>
          <h1 className="home__greeting">
            {nombre ? `${nombre}, tu equipo está listo.` : 'Tu equipo está listo.'}
          </h1>
          <p className="home__subtitle">
            Escribe lo que necesitas y {enrutadora} lo llevará al especialista adecuado.
            O abre una de las conversaciones en marcha.
          </p>
        </header>

        {/* HU-037 N-1b (#497) — AI Act art. 50.5: el momento es «la primera
            interacción O EXPOSICIÓN». Esta pantalla es las dos cosas antes que
            cualquier hilo: se ve el roster completo con foto, nombre propio y
            cargo humano (la mayor densidad de información contraria del
            producto) y hay un compositor desde el que se puede enviar el primer
            mensaje. Sin este aviso, el del hilo llega TARDE respecto del momento
            que fija la norma, no solo incompleto. Condición 9 de Erin
            (RESPUESTA-erin-linus-497-n1-lectura-implementacion §3) y §5 de la
            SPEC. Sin props: todavía no hay interlocutor -> variante de equipo. */}
        <AiDisclosureNotice />

        {/* Compositor de línea (Mesa): campo grande subrayado + botón redondo. */}
        <div className="home-composer">
          <div className="home-composer__field">
            <AutoGrowTextarea
              className="home-composer__input"
              placeholder="¿Qué necesitas hoy?"
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={onKeyDown}
              aria-label="Mensaje para tu equipo"
            />
            <button
              className="home-composer__send"
              onClick={enviar}
              disabled={!text.trim()}
              aria-label="Enviar"
            >
              <ArrowRight size={20} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
          <p className="home-composer__caption">
            {enrutadora} enruta · pulsa un agente abajo para dirigirte a él directamente.
            El recuadro crece con lo que escribas.
          </p>
        </div>

        {grupos.length > 0 && (
          <section className="home-team" aria-label="Tu equipo">
            <span className="home-team__overline">Tu equipo</span>
            {grupos.map(grupo => (
              <div className="home-dept" key={grupo.departamento}>
                {grupos.length > 1 && <h2 className="home-dept__title">{grupo.departamento}</h2>}
                <div className="home-dept__grid">
                  {grupo.agents.map(a => (
                    <TeamCard
                      key={a.id ?? a.nombre}
                      agent={a}
                      ringColor={agentColorOf(palette ?? {}, a.id, theme)}
                      onSelect={onSelectAgent}
                    />
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}

        {recientes.length > 0 && (
          <div className="home-recent">
            <div className="home-recent__head">
              <span className="home-recent__title">Continuar donde lo dejaste</span>
            </div>
            <div className="home-recent__grid">
              {recientes.map(c => (
                <ChatCard
                  key={c.chat_id}
                  chat={c}
                  agents={agents}
                  palette={palette}
                  theme={theme}
                  onOpen={onSelectChat}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
